import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Landmark, Percent, Receipt, Repeat, Wallet } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";

const FALLBACK = {
  otc: { service_fee_pct: 0.1, spread_buy_pct: 0.5, spread_sell_pct: 0.5 },
  p2p: { maker_pct: 0, taker_pct: 0.2 },
  withdrawals: [
    { asset: "BTC", network: "BTC", fee: "0.0005", unit: "BTC" },
    { asset: "USDT", network: "TRC20", fee: "1", unit: "USDT" },
  ],
  tax: {
    gst_rate_pct: 18,
    tds: {
      p2p: { tds_percentage: 1, minimum_amount_inr: 10000, deduct_from: "seller", enabled: true },
      otc: { tds_percentage: 1, minimum_amount_inr: 50000, deduct_from: "seller", enabled: true },
      escrow: { tds_percentage: 1, minimum_amount_inr: 10000, deduct_from: "seller", enabled: true },
    },
  },
};

const TDS_LABELS = { p2p: "P2P", otc: "OTC", escrow: "Escrow" };

const SECTIONS = [
  { id: "p2p", label: "P2P" },
  { id: "otc", label: "OTC" },
  { id: "withdrawals", label: "Withdrawals" },
  { id: "taxes", label: "Taxes" },
];

function pct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n}%`;
}

function fmtInr(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function RateTile({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-border bg-background/50 px-4 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="num mt-2 text-2xl font-semibold tracking-tight text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function FeeTable({ headers, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-2/60 text-left">
            {headers.map((h) => (
              <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-5 py-3.5 ${j > 0 ? "text-right tabular-nums text-foreground" : "font-medium text-foreground"}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProductCard({ id, icon: Icon, eyebrow, title, body, children, testid }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={testid || `fees-section-${id}`}>
      <div className="panel-gradient h-full rounded-2xl border border-border p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <span className="brand-gradient flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-primary-foreground">
            <Icon size={18} />
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">{eyebrow}</p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{title}</h2>
          </div>
        </div>
        <p className="mt-4 text-sm leading-7 text-muted-foreground">{body}</p>
        <div className="mt-5">{children}</div>
      </div>
    </section>
  );
}

export default function FeesPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/api/fees/schedule")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null));
  }, []);

  const f = data || FALLBACK;
  const withdrawals = f.withdrawals?.length ? f.withdrawals : FALLBACK.withdrawals;
  const tdsModules = Object.fromEntries(
    Object.entries(f.tax?.tds || FALLBACK.tax.tds).filter(([mod]) => mod in TDS_LABELS),
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="fees-page">
      <Header />

      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(var(--primary)/0.18),transparent_58%)]" />
        <div className="cm-container relative pb-12 pt-28 sm:pb-16">
          <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-primary">
            <Percent size={14} />
            Fee schedule
          </div>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Clear rates for P2P and OTC
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-8 text-muted-foreground">
            PaisaCX publishes the fee charged at execution. Network costs on withdrawals can change with the chain.
            Material changes are announced at least seven days in advance.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-border bg-surface/70 px-4 py-2 text-sm font-medium text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
              >
                {s.label}
              </a>
            ))}
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-3">
            <RateTile label="P2P taker" value={pct(f.p2p.taker_pct)} hint="Makers trade at no platform fee" />
            <RateTile label="OTC service fee" value={pct(f.otc.service_fee_pct)} hint="Plus the quoted buy or sell spread" />
            <RateTile label="GST on platform fees" value={pct(f.tax.gst_rate_pct)} hint="Where the law requires it" />
          </div>
        </div>
      </section>

      <div className="cm-container space-y-6 py-12 lg:py-16">
        <div className="grid gap-6 lg:grid-cols-2">
          <ProductCard
            id="p2p"
            icon={Repeat}
            eyebrow="Marketplace"
            title="P2P"
            body="Only the taker pays. Post an ad as a maker and the platform fee stays at zero."
          >
            <div className="grid gap-3 sm:grid-cols-2" data-testid="fees-p2p-table">
              <RateTile label="Maker" value={pct(f.p2p.maker_pct)} hint="Fee-free ads" />
              <RateTile label="Taker" value={pct(f.p2p.taker_pct)} hint="Charged when you take an ad" />
            </div>
          </ProductCard>

          <ProductCard
            id="otc"
            icon={Landmark}
            eyebrow="Desk"
            title="OTC"
            body="Quotes use a spread around the mid price, then a service fee on the trade value."
          >
            <div className="grid gap-3 sm:grid-cols-3" data-testid="fees-otc-table">
              <RateTile label="Service fee" value={pct(f.otc.service_fee_pct)} />
              <RateTile label="Buy spread" value={pct(f.otc.spread_buy_pct)} hint="Above mid" />
              <RateTile label="Sell spread" value={pct(f.otc.spread_sell_pct)} hint="Below mid" />
            </div>
          </ProductCard>
        </div>

        <ProductCard
          id="withdrawals"
          icon={Wallet}
          eyebrow="On-chain"
          title="Crypto withdrawals"
          body="A flat network fee is deducted from the amount you send. The figure depends on the asset and chain."
        >
          <FeeTable
            testid="fees-withdraw-table"
            headers={["Asset", "Network", "Fee"]}
            rows={withdrawals.map((w) => [w.asset, w.network, `${w.fee} ${w.unit}`])}
          />
        </ProductCard>

        <ProductCard
          id="taxes"
          icon={Receipt}
          eyebrow="India"
          title="Taxes and statutory charges"
          body="TDS under Section 194S applies when the trade value meets the minimum. GST may apply to platform service fees."
        >
          <FeeTable
            testid="fees-tax-table"
            headers={["Product", "TDS rate", "Min. trade value", "Deducted from"]}
            rows={Object.entries(tdsModules).map(([mod, t]) => [
              TDS_LABELS[mod],
              t.enabled === false ? "Disabled" : pct(t.tds_percentage),
              fmtInr(t.minimum_amount_inr),
              t.deduct_from === "seller" ? "Seller" : t.deduct_from,
            ])}
          />
          <p className="mt-4 text-sm leading-7 text-muted-foreground">
            GST on platform fees, where applicable: {pct(f.tax.gst_rate_pct)}. Signed-in traders can open their{" "}
            <Link to="/profile/tax" className="font-semibold text-primary hover:underline">
              TDS statement
            </Link>
            . The{" "}
            <Link to="/terms#fees" className="font-semibold text-primary hover:underline">
              Terms &amp; Conditions
            </Link>{" "}
            set out the full tax responsibilities.
          </p>
        </ProductCard>

        <div
          className="panel-gradient flex flex-col gap-4 rounded-2xl border border-primary/30 p-6 sm:flex-row sm:items-center sm:justify-between"
          data-testid="fees-cta"
        >
          <div>
            <div className="text-[15px] font-semibold">Ready to trade?</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Open the P2P marketplace or request a quote from the OTC desk.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/otc"
              className="inline-flex h-11 items-center justify-center rounded-lg border border-border px-5 text-sm font-semibold text-foreground transition hover:border-primary/40"
            >
              OTC desk
            </Link>
            <Link
              to="/p2p"
              className="brand-gradient inline-flex h-11 items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold text-primary-foreground transition hover:brightness-110"
              data-testid="fees-start-trading"
            >
              Start on P2P <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
